import type { StructuredBlock as StructuredBlockType } from "@containers/Hermes/types";
import HermesChart from "./HermesChart";
import ContactsResults from "./ContactsResults";
import ContactsBulkRequest from "./ContactsBulkRequest";
import useHermesAnalytics from "@containers/Hermes/hooks/useHermesAnalytics";

type StructuredBlockProps = {
  block: StructuredBlockType;
  onFollowUp?: (text: string, confirmToken?: string) => void;
  disabled?: boolean;
  // Needed by contacts_bulk_request's status lookup.
  conversationId?: string | null;
  messageId?: number;
};

export default function StructuredBlock({ block, onFollowUp, disabled, conversationId, messageId }: StructuredBlockProps) {
  if (!block || !block.type) return null;

  switch (block.type) {
    case "follow_up_suggestions":
      return <FollowUpSuggestions suggestions={block.suggestions ?? []} onFollowUp={onFollowUp} disabled={disabled} />;

    case "chart":
      return block.spec ? <HermesChart spec={block.spec} /> : null;

    // Offers are rendered as the message's action bars (ActionBars), not inline.
    case "download_offer":
    case "contacts_offer":
      return null;

    case "contacts_result":
      return block.deferred_to_email || block.results?.length ? <ContactsResults block={block} /> : null;

    case "contacts_bulk_request":
      return block.request_id !== null && block.request_id !== undefined && conversationId && messageId ? <ContactsBulkRequest block={block} conversationId={conversationId} messageId={messageId} /> : null;

    // Opened as an action card in place of the composer (ActionCard), not inline.
    case "credit_confirmation":
      return null;

    case "table":
      return null;

    default:
      return null;
  }
}

type FollowUpSuggestionsProps = {
  suggestions: string[];
  onFollowUp?: (text: string, confirmToken?: string) => void;
  disabled?: boolean;
};

function FollowUpSuggestions({ suggestions, onFollowUp, disabled }: FollowUpSuggestionsProps) {
  const analytics = useHermesAnalytics();

  if (!suggestions.length) return null;

  return (
    <div role="list" aria-label="Suggested follow-ups" className="tw-flex tw-flex-col tw-items-start tw-gap-2 tw-mt-4">
      {suggestions.map((text, i) => (
        <button
          key={text}
          role="listitem"
          disabled={disabled}
          onClick={() => {
            analytics.suggestionClicked({ suggestionType: "followup", text, position: i });
            if (onFollowUp) onFollowUp(text);
          }}
          className="tw-text-sm tw-text-left tw-text-gray-700 tw-px-4 tw-py-2 tw-rounded-full tw-border tw-border-gray-200 tw-bg-gray-25 hover:tw-border-primary-purple-300 hover:tw-bg-primary-purple-25 tw-transition-colors tw-duration-150 tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed disabled:hover:tw-border-gray-200 disabled:hover:tw-bg-gray-25">
          {text}
        </button>
      ))}
    </div>
  );
}
